import { cn } from "@lib/utils/helpers";
import { ConfigSubsection } from "./ConfigSubsection";
import type { YardiServerConfigSectionProps } from "../../../types";

export function YardiServerConfigSection({
    config,
    setConfig,
    yardiSecretPlaceholders,
    attachmentTypes,
    staffList,
    lrSaveAttempted,
    dataFetchRangeFieldError,
    dataFetchRangeTouched,
    onDataFetchRangeBlur,
}: YardiServerConfigSectionProps) {
    return (
        <ConfigSubsection title="Yardi Server Config" testId="company-config-yardi-server-section">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {/* User Name */}
            <div className="space-y-1.5">
                <label className="text-[13px] font-medium text-slate-600 block">User Name</label>
                <input
                    type="text"
                    data-testid="company-config-yardi-user-name-input"
                    value={config.user_name}
                    onChange={(e) => setConfig({ ...config, user_name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-[#eaeaea] rounded-md focus:outline-none focus:border-[#1597ff] transition-colors text-slate-700 placeholder:text-slate-400"
                    placeholder={yardiSecretPlaceholders.user_name || "Enter Username"}
                />
            </div>
            {/* Password */}
            <div className="space-y-1.5">
                <label className="text-[13px] font-medium text-slate-600 block">Password</label>
                <input
                    type="text"
                    data-testid="company-config-yardi-password-input"
                    value={config.password}
                    onChange={(e) => setConfig({ ...config, password: e.target.value })}
                    className="w-full px-4 py-2.5 border border-[#eaeaea] rounded-md focus:outline-none focus:border-[#1597ff] transition-colors text-slate-700 placeholder:text-slate-400"
                    placeholder={yardiSecretPlaceholders.password || "Enter Password"}
                />
            </div>
            {/* Server Name */}
            <div className="space-y-1.5">
                <label className="text-[13px] font-medium text-slate-600 block">Server Name</label>
                <input
                    type="text"
                    data-testid="company-config-yardi-server-name-input"
                    value={config.server_name}
                    onChange={(e) => setConfig({ ...config, server_name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-[#eaeaea] rounded-md focus:outline-none focus:border-[#1597ff] transition-colors text-slate-700 placeholder:text-slate-400"
                    placeholder={yardiSecretPlaceholders.server_name || "Enter Server Name"}
                />
            </div>
            {/* Database */}
            <div className="space-y-1.5">
                <label className="text-[13px] font-medium text-slate-600 block">Database</label>
                <input
                    type="text"
                    data-testid="company-config-yardi-database-input"
                    value={config.database}
                    onChange={(e) => setConfig({ ...config, database: e.target.value })}
                    className="w-full px-4 py-2.5 border border-[#eaeaea] rounded-md focus:outline-none focus:border-[#1597ff] transition-colors text-slate-700 placeholder:text-slate-400"
                    placeholder={yardiSecretPlaceholders.database || "Enter Database"}
                />
            </div>
            {/* Client URL */}
            <div className="space-y-1.5 lg:col-span-1">
                <label className="text-[13px] font-medium text-slate-600 block">Client url</label>
                <input
                    type="text"
                    data-testid="company-config-yardi-client-url-input"
                    value={config.client_url}
                    onChange={(e) => setConfig({ ...config, client_url: e.target.value })}
                    className="w-full px-4 py-2.5 border border-[#eaeaea] rounded-md focus:outline-none focus:border-[#1597ff] transition-colors text-slate-700"
                />
            </div>
            {/* Environment */}
            <div className="space-y-1.5">
                <label className="text-[13px] font-medium text-slate-600 block">Environment</label>
                <input
                    type="text"
                    data-testid="company-config-yardi-environment-input"
                    value={config.environment}
                    onChange={(e) => setConfig({ ...config, environment: e.target.value })}
                    className="w-full px-4 py-2.5 border border-[#eaeaea] rounded-md focus:outline-none focus:border-[#1597ff] transition-colors text-slate-700"
                />
            </div>
            {/* Data Fetch Range */}
            <div className="space-y-1.5">
                <label className="block text-[13px] font-medium text-slate-600">
                    Data Fetch Range By Days<span className="font-semibold text-[#dc2626]">*</span>
                </label>
                <input
                    type="text"
                    data-testid="company-config-yardi-data-fetch-range-days-input"
                    inputMode="numeric"
                    autoComplete="off"
                    value={config.data_fetch_range_by_days}
                    onChange={(e) => {
                        const next = e.target.value.replace(/\D/g, "")
                        setConfig({ ...config, data_fetch_range_by_days: next })
                    }}
                    onBlur={onDataFetchRangeBlur}
                    className={`w-full px-4 py-2.5 border rounded-md focus:outline-none transition-colors text-slate-700 shadow-inner ${
                        (lrSaveAttempted || dataFetchRangeTouched) && dataFetchRangeFieldError
                            ? "border-red-500 focus:border-red-500"
                            : "border-[#eaeaea] focus:border-[#1597ff]"
                    }`}
                />
                {(lrSaveAttempted || dataFetchRangeTouched) && dataFetchRangeFieldError && (
                    <div className="mt-1.5 flex items-start gap-2 text-[13px] text-red-600">
                        <span
                            className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-red-600 text-[11px] font-bold leading-none text-white"
                            aria-hidden
                        >
                            !
                        </span>
                        <span>{dataFetchRangeFieldError}</span>
                    </div>
                )}
            </div>
            {/* Lease Attachment Type */}
            <div className="space-y-1.5">
                <label className="text-[13px] font-medium text-slate-600 block">Lease Attachment Type</label>
                <select
                    value={config.lease_document_type}
                    onChange={(e) => setConfig({ ...config, lease_document_type: e.target.value })}
                    className="w-full px-4 py-2.5 border border-[#eaeaea] rounded-md focus:outline-none focus:border-[#1597ff] bg-white transition-colors text-slate-700"
                >
                    <option value="">Select Lease Attachment Type</option>
                    {attachmentTypes.map((type, idx: number) => (
                        <option key={idx} value={type.document_type}>{type.document_type}</option>
                    ))}
                </select>
            </div>
            {/* Version Number */}
            <div className="space-y-1.5">
                <label className="text-[13px] font-medium text-slate-600 block text-slate-400">Version Number</label>
                <input
                    type="text"
                    data-testid="company-config-yardi-version-number-input"
                    value={config.version_number}
                    readOnly
                    className="w-full px-4 py-2.5 border border-[#eaeaea] rounded-md bg-[#fcfcfc] text-slate-500 cursor-not-allowed"
                />
            </div>
            {/* Import PMC Data — Yes/No buttons like Payments tab "use for all properties" */}
            <div className="space-y-2">
                <span className="block text-sm font-medium text-slate-600">Import PMC Data</span>
                <div
                    className="inline-flex shrink-0 flex-wrap items-center gap-2.5"
                    role="group"
                    aria-label="Import PMC Data"
                >
                    <button
                        type="button"
                        data-testid="company-config-yardi-import-pmc-data-yes-button"
                        onClick={() => setConfig({ ...config, import_pmc_data: 1 })}
                        className={cn(
                            "inline-flex min-w-[4.5rem] items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium shadow-sm transition",
                            Number(config.import_pmc_data) === 1
                                ? "border-[rgb(21_151_255)] bg-[rgb(21_151_255)] text-white"
                                : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50",
                        )}
                    >
                        Yes
                    </button>
                    <button
                        type="button"
                        data-testid="company-config-yardi-import-pmc-data-no-button"
                        onClick={() => setConfig({ ...config, import_pmc_data: 0 })}
                        className={cn(
                            "inline-flex min-w-[4.5rem] items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium shadow-sm transition",
                            Number(config.import_pmc_data) === 0
                                ? "border-[rgb(21_151_255)] bg-[rgb(21_151_255)] text-white"
                                : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50",
                        )}
                    >
                        No
                    </button>
                </div>
            </div>
            {/* Select Agent */}
            <div className="space-y-1.5">
                <label className="block text-[13px] font-medium text-slate-600">
                    Select Agent<span className="font-semibold text-[#dc2626]">*</span>
                </label>
                <select
                    data-testid="company-config-yardi-agent-select"
                    value={config.agent_id}
                    onChange={(e) => setConfig({ ...config, agent_id: e.target.value })}
                    className={`w-full px-4 py-2.5 border rounded-md focus:outline-none bg-white transition-colors text-slate-700 ${
                        lrSaveAttempted && !String(config.agent_id ?? "").trim()
                            ? "border-red-500 focus:border-red-500"
                            : "border-[#eaeaea] focus:border-[#1597ff]"
                    }`}
                >
                    <option value="">Choose Agent...</option>
                    {staffList.map((agent) => (
                        <option key={agent.user_id} value={agent.user_id}>{agent.name}</option>
                    ))}
                </select>
                {lrSaveAttempted && staffList.length === 0 && (
                    <div className="mt-1.5 flex items-start gap-2 text-[13px] text-red-600">
                        <span
                            className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-red-600 text-[11px] font-bold leading-none text-white"
                            aria-hidden
                        >
                            !
                        </span>
                        <span>No company agents are available.</span>
                    </div>
                )}
                {lrSaveAttempted && staffList.length > 0 && !String(config.agent_id ?? "").trim() && (
                    <div className="mt-1.5 flex items-start gap-2 text-[13px] text-red-600">
                        <span
                            className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-red-600 text-[11px] font-bold leading-none text-white"
                            aria-hidden
                        >
                            !
                        </span>
                        <span>Agent is required.</span>
                    </div>
                )}
            </div>
        </div>
        </ConfigSubsection>
    );
}
